import { useEffect, useMemo, useRef, useState } from "react";
import { listEvents, splitEvents } from "@/api/events";
import { env } from "@/config/env";
import { SITE } from "@/config/site";
import { useMediaQuery, useNow, useParallax, useReducedMotion } from "@/hooks/useMotion";
import { useResource } from "@/hooks/useResource";
import { cn } from "@/lib/cn";
import { churchDayParts, formatChurchTime, joinTimes, upcomingServices } from "@/lib/schedule";
import { Link } from "@/router/Router";
import { useLive } from "@/state/LiveProvider";
import { Icon } from "@/components/ui/Icon";
import { YouTubeBackground } from "@/components/media/YouTubeBackground";
import { SplitText } from "@/components/motion/Reveal";

/**
 * Full-screen cinematic hero: Pastor Jeffrey preaching, muted and looping, in black and white.
 *
 * Background, in order of preference:
 *   1. A YouTube video (env.heroYoutube: set VITE_HERO_YOUTUBE to change or "off" to disable).
 *      The photo stays up until the video is really playing, then they cross-fade.
 *   2. A self-hosted mp4 (asset contract below), used when YouTube is off, blocked or fails,
 *      and by visitors on data-saver or reduced motion (who get the photo only).
 *   3. The photo, with a slow drift.
 *
 * Asset contract (see public/media/README.md):
 *   public/media/pastor-jeffrey-hero.mp4         desktop (1920×1080, ~6–10 MB, H.264, no audio)
 *   public/media/pastor-jeffrey-hero-mobile.mp4  optional portrait/lighter cut for phones
 *   public/media/pastor-jeffrey-poster.jpg       photo of Pastor Jeffrey: shown while the video
 *                                                loads and as the fallback if there is no video
 *   public/media/pastor-jeffrey.jpg              alternate filename for the same photo
 *
 * The <video> only renders after the file is confirmed to exist, so there is never a broken
 * player. Without a video the photo shows with a slow drift; without a photo, a light-and-flame
 * composition. Reduced motion: photo only, no autoplay.
 */

type HeroMedia = { video: string | null; poster: string | null; checked: boolean };

/** Photo candidates, tried in order. The first one that loads is used. */
const POSTER_CANDIDATES = Array.from(
  new Set([env.heroPoster, "/media/pastor-jeffrey.jpg", "/media/pastor-jeffrey.jpg"].filter(Boolean)),
) as string[];

async function exists(url: string, kind: "video" | "image"): Promise<boolean> {
  if (!url) return false;
  if (kind === "image") {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth > 0);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }
  try {
    const res = await fetch(url, { method: "HEAD" });
    const type = res.headers.get("content-type") ?? "";
    return res.ok && (type.startsWith("video/") || type === "application/octet-stream");
  } catch {
    return false;
  }
}

async function firstExisting(urls: string[], kind: "video" | "image"): Promise<string | null> {
  for (const url of urls) {
    if (await exists(url, kind)) return url;
  }
  return null;
}

function useHeroMedia(isPhone: boolean, reduced: boolean, skipVideo: boolean): HeroMedia {
  const [media, setMedia] = useState<HeroMedia>({ video: null, poster: null, checked: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const poster = await firstExisting(POSTER_CANDIDATES, "image");
      let video: string | null = null;
      if (!reduced && !skipVideo) {
        if (isPhone && (await exists(env.heroVideoMobile, "video"))) video = env.heroVideoMobile;
        else if (await exists(env.heroVideo, "video")) video = env.heroVideo;
      }
      if (!cancelled) setMedia({ video, poster, checked: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [isPhone, reduced, skipVideo]);
  return media;
}

export function Hero() {
  const reduced = useReducedMotion();
  const isPhone = useMediaQuery("(max-width: 700px)");
  const [ytFailed, setYtFailed] = useState(false);
  const saveData = typeof navigator !== "undefined" && Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
  const ytActive = Boolean(env.heroYoutube) && !reduced && !saveData && !ytFailed;
  const media = useHeroMedia(isPhone, reduced, ytActive);
  const [playing, setPlaying] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [inView, setInView] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const stage = useParallax<HTMLDivElement>(0.16);
  const { status } = useLive();
  const live = status?.isLive === true;

  // Track whether the hero is on screen, to pause the YouTube video when it is not.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Pause the video when the hero is off screen (saves battery and data).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    });
    io.observe(v);
    return () => io.disconnect();
  }, [media.video]);

  const showVideo = !ytActive && media.video && !videoFailed;

  return (
    <section ref={sectionRef} className={cn("hero", playing && "is-playing", (media.checked || ytActive) && "is-ready", ytActive && "has-youtube")} aria-labelledby="hero-title">
      <div ref={stage} className="hero__stage" aria-hidden>
        {media.poster ? <img className={cn("hero__poster", !showVideo && !ytActive && !reduced && "hero__poster--drift")} src={media.poster} alt="" /> : <HeroAtmosphere />}
        {ytActive ? (
          <YouTubeBackground
            className="hero__yt"
            videoId={env.heroYoutube}
            start={env.heroYoutubeStart}
            loopSeconds={env.heroYoutubeLoop}
            active={inView}
            onPlaying={() => setPlaying(true)}
            onFail={() => setYtFailed(true)}
          />
        ) : null}
        {showVideo ? (
          <video
            ref={videoRef}
            className="hero__video"
            src={media.video ?? undefined}
            poster={media.poster ?? undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            onPlaying={() => setPlaying(true)}
            onError={() => setVideoFailed(true)}
          />
        ) : null}
        <div className="hero__tint" />
        <div className="hero__grade" />
        <div className="hero__grain" />
      </div>

      <div className="container hero__content">
        <span className="hero__rule" aria-hidden />
        <h1 id="hero-title" className="hero__title">
          <SplitText text="Liberating people" className="hero__line" step={80} />
          <SplitText text="for quality living." className="hero__line hero__line--accent" delay={200} step={80} />
        </h1>
        <p className="hero__lead">
          A Spirit &amp; Word church in Benin City. We lift up Jesus, expose you to His Word and His power — and lives are changed. Just one visit will change your life forever.
        </p>
        <div className="hero__actions">
          <Link to="/new" className="hero-btn hero-btn--outline">
            Plan your visit
          </Link>
          <Link to="/watch/live" className={cn("hero-watch", live && "is-live")}>
            <span className="hero-watch__orb" aria-hidden>
              {live ? <span className="live-dot" /> : <Icon name="play" size={14} />}
            </span>
            {live ? "We're live — watch now" : "Watch live"}
          </Link>
        </div>
      </div>

      <Happening />

      {/* Credit shows over the video or the photo. Hidden only when there is neither. */}
      {showVideo || ytActive || media.poster ? <p className="hero__credit">{SITE.pastor}</p> : null}
    </section>
  );
}

/** Shown when there is no photo yet: stage light, flame glow and a quiet floor grid. */
function HeroAtmosphere() {
  return (
    <div className="hero-atmos">
      <span className="hero-atmos__beam" />
      <span className="hero-atmos__beam hero-atmos__beam--2" />
      <span className="hero-atmos__flame" />
      <span className="hero-atmos__floor" />
      <span className="hero-atmos__figure" />
    </div>
  );
}

const SERVICE_BLURB = {
  celebration: "Praise, worship and the Word — a celebration of all God is doing.",
  word: "Our midweek service: deep, practical teaching from the Scriptures.",
  academy: "Membership, Maturity, Ministry and Missions classes.",
} as const;

type Card = { key: string; when: Date; title: string; text: string; href: string; tag?: string; live?: boolean };

/**
 * "What's happening" — three date cards along the foot of the hero: the live service (only when
 * the API says so), upcoming programmes, then the next weekly services.
 */
function Happening() {
  const { status } = useLive();
  const now = useNow(60_000);
  const events = useResource("events", (signal) => listEvents(signal));

  const cards = useMemo<Card[]>(() => {
    const list: Card[] = [];
    if (status?.isLive) {
      list.push({ key: "live", when: now, title: status.title, text: "We're live right now. Join the service online.", href: "/watch/live", tag: "Live now", live: true });
    }
    const upcoming = events.data ? splitEvents(events.data.events, now.getTime()).upcoming : [];
    for (const e of upcoming.slice(0, 2)) {
      list.push({
        key: `e-${e.id}`,
        when: new Date(e.startsAt),
        title: e.subtitle ? `${e.title}: ${e.subtitle}` : e.title,
        text: e.description,
        href: `/events/${encodeURIComponent(e.id)}`,
        tag: e.location ?? "Programme",
      });
    }
    for (const s of upcomingServices(now, 14)) {
      if (list.length >= 3) break;
      if (status?.isLive && s.startsAt.getTime() <= now.getTime()) continue;
      list.push({
        key: `s-${s.name}-${s.startsAt.getTime()}`,
        when: s.startsAt,
        title: s.name,
        text: `${joinTimes(s.times)} WAT · ${SERVICE_BLURB[s.kind]}`,
        href: s.kind === "academy" ? "/connect/believers-academy" : "/calendar",
        tag: s.startsAt.getTime() <= now.getTime() ? "Happening now" : formatChurchTime(s.startsAt).split(" ")[0],
      });
    }
    return list.slice(0, 3);
  }, [status, events.data, now]);

  return (
    <div className="happening">
      <div className="container">
        <div className="happening__head">
          <p className="happening__label">What's happening</p>
          <Link to="/calendar" className="happening__all">
            Church calendar <Icon name="arrowRight" size={14} />
          </Link>
        </div>
        <ol className="happening__list">
          {cards.map((c, i) => {
            const d = churchDayParts(c.when);
            return (
              <li key={c.key} className="happening__item" style={{ animationDelay: `${1000 + i * 120}ms` }}>
                <Link to={c.href} className={cn("happening-card", c.live && "is-live")}>
                  <span className="happening-card__date" aria-hidden>
                    {c.live ? (
                      <span className="happening-card__live">
                        <span className="live-dot" />
                        Live
                      </span>
                    ) : (
                      <>
                        <span className="happening-card__mon">{d.month}</span>
                        <span className="happening-card__day">{d.day}</span>
                      </>
                    )}
                  </span>
                  <span className="happening-card__body">
                    <span className="happening-card__tag">{c.live ? "Live now" : `${d.weekday}${c.tag && c.tag !== d.weekday ? ` · ${c.tag}` : ""}`}</span>
                    <span className="happening-card__title">{c.title}</span>
                    <span className="happening-card__text">{c.text}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}